import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export interface FeeType {
  id: number;
  name: string;
  schoolId?: number;
  createdAt?: string;
}

export interface FeeInstallmentDto {
  feeTypeId: number;
  feeTypeName: string;
  amount: number;
  dueDate: string; // ISO date string
}

export interface FeeStructureDto {
  id?: number;
  sessionId: number;
  classId: number;
  totalAnnualFees: number;
  totalInstallments: number;
  feeInstallments: FeeInstallmentDto[];
  className?: string;
  sessionName?: string;
}

export interface BankDetailsDto {
  id: number; // school id expected by backend
  bankName: string;
  accountNo: string;
  ifscCode: string;
}

const API_URL = `${process.env.REACT_APP_API_URL}/feeStructure`;
const FEE_TYPES_API_URL = `${process.env.REACT_APP_API_URL}/fee-types`;

// Fetch all fee structures (old endpoint with schoolId)
const getAllFeeStructures = async (schoolId?: number): Promise<FeeStructureDto[]> => {
  try {
    const response = await axios.get(API_URL, {
      params: schoolId ? { schoolId } : undefined,
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404 || error?.response?.status === 500) {
      return [];
    }
    throw error;
  }
};

// Fetch all fee structures (GET /feeStructure/get endpoint)
const getAllFeeStructuresNew = async (): Promise<FeeStructureDto[]> => {
  try {
    const response = await axios.get(`${API_URL}/get`);
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  } catch (error: any) {
    if (error?.response?.status === 404 || error?.response?.status === 500) {
      return [];
    }
    if (error?.response?.status === 401) {
      return [];
    }
    throw error;
  }
};

// Fetch fee structure by sessionId and classId
const getFeeStructureBySessionAndClass = async (sessionId: number, className: String): Promise<FeeStructureDto | null> => {
  try {
    const response = await axios.get(`${API_URL}/getBySessionAndClass`, {
      params: { sessionId, className }
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    if (error?.response?.status === 401) {
      throw error;
    }
    return null;
  }
};

// Create a new fee structure
const createFeeStructure = async (data: FeeStructureDto): Promise<FeeStructureDto> => {
  const response = await axios.post(`${API_URL}/add`, data);
  return response.data;
};

// Update a fee structure
const updateFeeStructure = async (data: FeeStructureDto): Promise<FeeStructureDto> => {
  const response = await axios.put(API_URL, data);
  return response.data;
};

// Delete a fee structure
const deleteFeeStructure = async (sessionId: number, classId: number): Promise<void> => {
  await axios.delete(API_URL, {
    params: { sessionId, classId }
  });
};

// Add bank details
const addBankDetails = async (data: BankDetailsDto): Promise<any> => {
  const response = await axios.post(`${API_URL}/addBankDetails`, data);
  return response.data;
};

// Fetch bank details for a school
const getBankDetails = async (schoolId: number): Promise<BankDetailsDto | null> => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/schools/${schoolId}`);
    const schoolData = response.data;

    if (schoolData.bankName || schoolData.accountNo || schoolData.ifscCode) {
      return {
        id: schoolId,
        bankName: schoolData.bankName || "",
        accountNo: schoolData.accountNo || "",
        ifscCode: schoolData.ifscCode || "",
      };
    }
    return null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Fetch available fee types
const getFeeTypes = async (schoolId?: number): Promise<FeeType[]> => {
  const response = await axios.get(FEE_TYPES_API_URL, {
    params: schoolId ? { schoolId } : undefined,
  });
  return response.data;
};

// Create custom fee type
const createFeeType = async (data: { name: string; schoolId?: number }): Promise<FeeType> => {
  const response = await axios.post(FEE_TYPES_API_URL, data);
  return response.data;
};

// Delete custom fee type
const deleteFeeType = async (id: number): Promise<void> => {
  await axios.delete(`${FEE_TYPES_API_URL}/${id}`);
};

export function useFeeStructure(schoolId?: number, tenantLoading: boolean = false, enableQuery: boolean = false) {
  const queryClient = useQueryClient();

  const feeStructuresQuery = useQuery<FeeStructureDto[], Error>({
    queryKey: ["feeStructures", schoolId],
    queryFn: () => getAllFeeStructures(schoolId),
    enabled: false,
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const feeStructuresGetQuery = useQuery<FeeStructureDto[], Error>({
    queryKey: ["feeStructures", "get"],
    queryFn: () => getAllFeeStructuresNew(),
    enabled: enableQuery && !tenantLoading,
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const createFeeStructureMutation = useMutation({
    mutationFn: createFeeStructure,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructures", "get"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructure", "bySessionAndClass"] });
      queryClient.invalidateQueries({ queryKey: ["feeTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
    },
  });

  const updateFeeStructureMutation = useMutation({
    mutationFn: updateFeeStructure,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructures", "get"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructure", "bySessionAndClass"] });
      queryClient.invalidateQueries({ queryKey: ["feeTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
    },
  });

  const deleteFeeStructureMutation = useMutation({
    mutationFn: ({ sessionId, classId }: { sessionId: number; classId: number }) =>
      deleteFeeStructure(sessionId, classId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructures", "get"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructure", "bySessionAndClass"] });
      queryClient.invalidateQueries({ queryKey: ["feeTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["studentAccounts"] });
    },
  });

  const addBankDetailsMutation = useMutation({
    mutationFn: addBankDetails,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schoolById"] });
      queryClient.invalidateQueries({ queryKey: ["schools"] });
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
    },
  });

  const bankDetailsQuery = useQuery<BankDetailsDto | null, Error>({
    queryKey: ["bankDetails", schoolId],
    queryFn: () => schoolId ? getBankDetails(schoolId) : Promise.resolve(null),
    enabled: !!schoolId,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    feeStructuresQuery,
    feeStructuresGetQuery,
    createFeeStructureMutation,
    updateFeeStructureMutation,
    deleteFeeStructureMutation,
    addBankDetailsMutation,
    bankDetailsQuery,
    getFeeStructureBySessionAndClass,
  };
}

export function useFeeStructureBySessionAndClass(sessionId?: number, className?: String, enabled: boolean = true) {
  return useQuery<FeeStructureDto | null, Error>({
    queryKey: ["feeStructure", "bySessionAndClass", sessionId, className],
    queryFn: () => {
      if (!sessionId || !className) {
        return Promise.resolve(null);
      }
      return getFeeStructureBySessionAndClass(sessionId, className);
    },
    enabled: enabled && !!sessionId && !!className,
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useFeeTypes(schoolId?: number, enabled: boolean = true) {
  return useQuery<FeeType[], Error>({
    queryKey: ["feeTypes", schoolId],
    queryFn: () => getFeeTypes(schoolId),
    enabled: enabled && !!schoolId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

export function useCreateFeeType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFeeType,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["feeTypes", variables.schoolId] });
    },
  });
}

export function useDeleteFeeType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFeeType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feeTypes"] });
    },
  });
}
